import React from "react";
import {
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Badge,
} from "@mui/material";
import { useLocation, Link as RouterLink } from "react-router-dom";
import useThemeStore from "../services/store/useThemeStore";
import { useTranslation } from "react-i18next";

interface SidebarNavItemProps {
  item: {
    type?: string;
    text: string;
    icon: JSX.Element;
    path: string;
    fontSize?: string;
  };
  open: boolean;
  selected?: boolean;
  onClick?: () => void;
  badgeContent?: number;
}

const SidebarNavItem: React.FC<SidebarNavItemProps> = ({
  item,
  open,
  selected,
  onClick,
  badgeContent,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const isActive = selected ?? location.pathname === item.path;

  const { darkMode: isDarkMode } = useThemeStore();

  return (
    <Tooltip
      title={!open ? t(item.text) : ""}
      placement="right"
      arrow
      enterDelay={500}
    >
      <ListItemButton
        component={RouterLink}
        to={item.path}
        onClick={onClick}
        selected={isActive}
        aria-current={isActive ? "page" : undefined}
        sx={{
          borderRadius: 3,
          minHeight: 50,
          mx: 1,
          justifyContent: open ? "flex-start" : "center",
          px: open ? 2.5 : 1.5,
          py: 1.2,
          fontFamily: "'Lexend', sans-serif",
          transition: "all 0.2s ease",
          backgroundColor: isActive ? "var(--primary-100)" : "transparent",
          color: isActive
            ? "var(--primary-700)"
            : isDarkMode
              ? "var(--neutral-100)"
              : "var(--neutral-1000)",
          border: "1px solid transparent",
          "&:hover": {
            backgroundColor: isActive
              ? "var(--primary-200)"
              : isDarkMode
                ? "var(--neutral-800)"
                : "var(--neutral-100)",
            color: "var(--primary-700)",
            transform: "translateY(-1px)",
            boxShadow: "var(--shadow-sm)",
            "& .MuiListItemIcon-root": {
              color: "var(--primary-700)",
            },
          },
          "&.Mui-selected:hover": {
            backgroundColor: "var(--primary-200)",
          },
          "&.Mui-selected": {
            boxShadow: "var(--shadow-sm)",
            backgroundColor: "var(--primary-200)",
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: open ? 1.5 : 0,
            justifyContent: "center",
            color: isActive
              ? "var(--primary-700)"
              : isDarkMode
                ? "var(--neutral-100)"
                : "var(--neutral-1000)",
            transition: "all 0.2s ease",
            fontSize: "1.1rem",
          }}
        >
          {badgeContent && badgeContent > 0 ? (
            <Badge
              color="error"
              badgeContent={badgeContent}
              max={99}
              overlap="circular"
            >
              {item.icon}
            </Badge>
          ) : (
            item.icon
          )}
        </ListItemIcon>

        {open && (
          <ListItemText
            primary={t(item.text)}
            sx={{
              "& .MuiListItemText-primary": {
                fontWeight: isActive ? 600 : 500,
                fontSize: item.fontSize ?? "0.875rem",
                fontFamily: "'Lexend', sans-serif",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: "inherit",
              },
            }}
          />
        )}
      </ListItemButton>
    </Tooltip>
  );
};

export default SidebarNavItem;
